// emailHtml.js — renders a newsletter issue (stored in the blog_structured
// shape + `newsletter` extras) to email-client-safe HTML and plain text.
//
// Why a separate renderer from blog/cleanHtml.js: Gmail/Outlook ignore scoped
// <style> blocks, CSS variables, <details>, flexbox and most modern layout.
// So everything here is table based with inline styles (the <style> block in
// the exported document is only a progressive enhancement for mobile). Same
// brand tokens and the same inline block syntax (callouts, tables, lists) as
// the blog, so one issue looks like a sibling of the blog article.

import { escapeHtml } from '../blog/cleanHtml';

export const NL_TOKENS = {
  navy: '#0a1628',
  teal: '#1e9ead',
  tealDark: '#0d7278',
  ink: '#26313f',
  muted: '#5a6c7d',
  border: '#e3e8ef',
  soft: '#f6f8fa',
  font: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif",
};

const T = NL_TOKENS;

const NL_LABELS = {
  de: {
    toc: 'In dieser Ausgabe', takeaways: 'Kurz gesagt', conclusion: 'Fazit', faq: 'Kurz beantwortet',
    readingTime: 'Min. Lesezeit', issue: 'Ausgabe', defaultCta: 'Mehr erfahren',
    why: 'Du erhältst diese E-Mail, weil du dich für unseren Newsletter angemeldet hast.',
    unsubscribe: 'Abmelden', imprint: 'Impressum', webview: 'Im Browser ansehen',
    info: 'Info', einordnung: 'Einordnung', important: 'Wichtig', summary: 'Zusammenfassung',
  },
  en: {
    toc: 'In this issue', takeaways: 'In short', conclusion: 'Conclusion', faq: 'Quick answers',
    readingTime: 'min read', issue: 'Issue', defaultCta: 'Learn more',
    why: 'You are receiving this email because you subscribed to our newsletter.',
    unsubscribe: 'Unsubscribe', imprint: 'Legal notice', webview: 'View in browser',
    info: 'Info', einordnung: 'Context', important: 'Important', summary: 'Summary',
  },
};

export const getNewsletterLabels = (lang) => NL_LABELS[lang === 'en' ? 'en' : 'de'];

const CALLOUT_COLORS = {
  info: { bar: T.teal, bg: '#eef8f9', label: T.tealDark },
  einordnung: { bar: '#6d5ae6', bg: '#f4f2fd', label: '#574bd0' },
  important: { bar: '#d9534f', bg: '#fdf3f2', label: '#c0392b' },
  summary: { bar: T.navy, bg: '#f2f5f8', label: T.navy },
};

const p = (inner, extra = '', path = '') =>
  `<p${nlAttr(path)} style="margin:0 0 16px 0;font-family:${T.font};font-size:16px;line-height:1.65;color:${T.ink};${extra}">${inner}</p>`;

// A callout glued to the end of a paragraph ("... Aufwand. > INFO: ...") must
// become its own block, otherwise the marker leaks into the text.
const INLINE_CALLOUT = /([^\n>])[ \t]*\n?[ \t]*(>\s*(?:INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):)/gi;
const splitInlineCallouts = (t) => String(t || '').replace(INLINE_CALLOUT, '$1\n\n$2');

const splitBlocks = (text) => splitInlineCallouts(text).split(/\n\s*\n/).filter(Boolean);

// data-nl="<path>" marks every text the user may type into inside the preview.
const nlAttr = (path) => (path ? ` data-nl="${path}"` : '');

// Same block grammar as blog/cleanHtml.paragraphsToHtml, emitted as inline-styled
// email markup.
export const paragraphsToEmailHtml = (text, L, editPath) => {
  if (!text) return '';
  return splitBlocks(text).map((block, bi) => {
    const bp = editPath ? `${editPath}:${bi}` : '';
    const lines = block.split(/\n/);

    const callout = block.match(/^\s*>\s*(INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):\s*/i);
    if (callout) {
      const raw = callout[1].toUpperCase();
      const body = block.slice(callout[0].length).replace(/\n>\s?/g, '\n').trim();
      let type = 'summary';
      if (raw === 'INFO') type = 'info';
      else if (raw === 'EINORDNUNG') type = 'einordnung';
      else if (['WICHTIG', 'IMPORTANT', 'WARNING', 'ACHTUNG'].includes(raw)) type = 'important';
      const c = CALLOUT_COLORS[type];
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;"><tr>
<td style="background:${c.bg};border-left:4px solid ${c.bar};padding:14px 18px;font-family:${T.font};">
<div style="font-size:11px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:${c.label};margin:0 0 6px 0;">${escapeHtml(L[type])}</div>
<div${nlAttr(bp)} style="font-size:15px;line-height:1.6;color:${T.ink};">${escapeHtml(body).replace(/\n/g, '<br>')}</div>
</td></tr></table>`;
    }

    const isTable = lines.length >= 2
      && /^\s*\|.*\|\s*$/.test(lines[0])
      && /^\s*\|?\s*[-:|\s]+\s*\|?\s*$/.test(lines[1] || '');
    if (isTable) {
      const cells = (row) => row.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
      const head = cells(lines[0]);
      const rows = lines.slice(2).filter((l) => /\|/.test(l)).map(cells);
      const th = head.map((c) => `<th align="left" style="background:${T.navy};color:#ffffff;font-family:${T.font};font-size:14px;font-weight:600;padding:10px 12px;">${escapeHtml(c)}</th>`).join('');
      const tr = rows.map((r, i) => `<tr>${r.map((c) => `<td valign="top" style="background:${i % 2 ? T.soft : '#ffffff'};border-top:1px solid ${T.border};font-family:${T.font};font-size:14px;line-height:1.5;color:${T.ink};padding:9px 12px;">${escapeHtml(c)}</td>`).join('')}</tr>`).join('');
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 20px 0;border:1px solid ${T.border};border-collapse:collapse;"><tr>${th}</tr>${tr}</table>`;
    }

    if (lines.every((l) => l.trim() === '' || /^\s*\d+\.\s+/.test(l)) && lines.some((l) => /^\s*\d+\.\s+/.test(l))) {
      const rows = lines.filter((l) => /^\s*\d+\.\s+/.test(l)).map((l, i) => `<tr>
<td valign="top" width="36" style="padding:0 0 12px 0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" width="26" height="26" style="width:26px;height:26px;background:${T.teal};border-radius:13px;color:#ffffff;font-family:${T.font};font-size:13px;font-weight:700;line-height:26px;">${i + 1}</td></tr></table></td>
<td valign="top"${nlAttr(bp && `${bp}:${i}`)} style="padding:2px 0 12px 0;font-family:${T.font};font-size:16px;line-height:1.6;color:${T.ink};">${escapeHtml(l.replace(/^\s*\d+\.\s+/, ''))}</td></tr>`).join('');
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 8px 0;">${rows}</table>`;
    }

    if (/^\s*-\s+/.test(block)) {
      const rows = lines.filter((l) => /^\s*-\s+/.test(l)).map((l, i) => `<tr>
<td valign="top" width="20" style="padding:0 0 8px 0;font-family:${T.font};font-size:16px;line-height:1.6;color:${T.teal};">&bull;</td>
<td valign="top"${nlAttr(bp && `${bp}:${i}`)} style="padding:0 0 8px 0;font-family:${T.font};font-size:16px;line-height:1.6;color:${T.ink};">${escapeHtml(l.replace(/^\s*-\s+/, ''))}</td></tr>`).join('');
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 12px 0;">${rows}</table>`;
    }

    return p(escapeHtml(block).replace(/\n/g, '<br>'), '', bp);
  }).join('\n');
};

const h2 = (text, id, path = '') =>
  `<h2${id ? ` id="${id}"` : ''}${nlAttr(path)} style="margin:32px 0 12px 0;font-family:${T.font};font-size:22px;line-height:1.3;font-weight:700;color:${T.navy};">${escapeHtml(text)}</h2>`;

const button = (label, url, path = '') => `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="margin:8px 0 8px 0;"><tr>
<td align="center" bgcolor="${T.teal}" style="background:${T.teal};border-radius:8px;">
<a href="${escapeHtml(url || '#')}"${nlAttr(path)} target="_blank" style="display:inline-block;padding:15px 30px;font-family:${T.font};font-size:16px;font-weight:700;line-height:1.2;color:#ffffff;text-decoration:none;border-radius:8px;">${escapeHtml(label)}</a>
</td></tr></table>`;

const secTitle = (s) => s?.h2 || s?.header || s?.headline || '';
const secBody = (s) => s?.content || s?.body || '';

export function getNewsletterMeta(blogData) {
  const nl = blogData?.newsletter || {};
  return {
    subject: nl.subject || blogData?.meta?.title || '',
    preheader: nl.preheader || '',
    ctaLabel: nl.cta_label || '',
    ctaLead: nl.cta_lead || '',
  };
}

/**
 * Render the issue.
 * opts: { lang, fallbackTitle, heroImageUrl, ctaUrl, brandName, logoUrl,
 *         issueDate, senderAddress, unsubscribeUrl, imprintUrl, webviewUrl,
 *         standalone (default true -> full <html> document) }
 */
export function buildNewsletterHtml(blogData, opts = {}) {
  const lang = opts.lang === 'en' ? 'en' : 'de';
  const L = getNewsletterLabels(lang);
  const ep = (path) => (opts.editable ? path : '');
  const meta = blogData?.meta || {};
  const nl = getNewsletterMeta(blogData);
  const title = meta.title || opts.fallbackTitle || nl.subject || '';
  const sections = (blogData?.sections || []).filter((s) => s && (secBody(s) || secTitle(s)));
  const takeaways = (blogData?.key_takeaways || []).filter(Boolean);
  const faq = (blogData?.faq || []).filter((f) => f && (f.question || f.answer));
  const intro = blogData?.intro || '';
  const conclusion = blogData?.conclusion || '';
  const brand = opts.brandName || 'xchannels.ai';
  const date = opts.issueDate || new Date().toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
  const ctaLabel = nl.ctaLabel || L.defaultCta;
  const unsub = opts.unsubscribeUrl || '{{unsubscribe_url}}';
  const hero = /^https?:\/\//i.test((opts.heroImageUrl || '').trim()) ? opts.heroImageUrl.trim() : '';

  const preheader = nl.preheader
    ? `<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;mso-hide:all;">${escapeHtml(nl.preheader)}${'&nbsp;&zwnj;'.repeat(40)}</div>`
    : '';

  const headerLeft = opts.logoUrl && /^https?:\/\//i.test(opts.logoUrl)
    ? `<img src="${escapeHtml(opts.logoUrl)}" alt="${escapeHtml(brand)}" height="32" style="display:block;height:32px;width:auto;border:0;">`
    : `<span style="font-family:${T.font};font-size:18px;font-weight:800;color:${T.navy};letter-spacing:-0.01em;">${escapeHtml(brand)}</span>`;
  const readingTime = meta.reading_time_minutes ? ` &middot; ${escapeHtml(String(meta.reading_time_minutes))} ${L.readingTime}` : '';

  const header = `<tr><td style="padding:28px 32px 20px 32px;border-bottom:1px solid ${T.border};">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
<td align="left" valign="middle">${headerLeft}</td>
<td align="right" valign="middle" style="font-family:${T.font};font-size:13px;color:${T.muted};">${escapeHtml(date)}${readingTime}</td>
</tr></table></td></tr>`;

  const heroRow = hero
    ? `<tr><td style="padding:0;"><img src="${escapeHtml(hero)}" alt="${escapeHtml(blogData?.image_alt_suggestion || title)}" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;"></td></tr>`
    : '';

  // Light, borderless blocks: a 300–500 word mail must not feel like a form.
  const label = (t) => `<div style="font-size:12px;font-weight:700;letter-spacing:0.07em;text-transform:uppercase;color:${T.tealDark};margin:0 0 8px 0;">${escapeHtml(t)}</div>`;
  const toc = sections.length >= 3
    ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 24px 0;"><tr>
<td style="padding:4px 0 4px 16px;border-left:3px solid ${T.border};font-family:${T.font};">
${label(L.toc)}
${sections.map((s, i) => `<div style="font-size:15px;line-height:1.5;margin:0 0 3px 0;color:${T.ink};">${i + 1}.&nbsp;<a href="#nl-section-${i}" style="color:${T.navy};text-decoration:underline;">${escapeHtml(secTitle(s) || `${i + 1}`)}</a></div>`).join('')}
</td></tr></table>`
    : '';

  const takeawayBox = takeaways.length
    ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:0 0 8px 0;"><tr>
<td style="padding:4px 0 4px 16px;border-left:3px solid ${T.teal};font-family:${T.font};">
${label(L.takeaways)}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${takeaways.map((k, ki) => `<tr><td valign="top" width="18" style="padding:0 0 6px 0;font-size:16px;line-height:1.55;color:${T.teal};">&bull;</td><td valign="top"${nlAttr(ep(`takeaway:${ki}`))} style="padding:0 0 6px 0;font-size:16px;line-height:1.55;color:${T.ink};">${escapeHtml(k)}</td></tr>`).join('')}</table>
</td></tr></table>`
    : '';

  const body = sections.map((s, i) => {
    const h3s = (s.h3_blocks || []).filter((b) => b && (b.content || b.h3)).map((b) =>
      `${b.h3 ? `<h3 style="margin:20px 0 8px 0;font-family:${T.font};font-size:18px;line-height:1.35;font-weight:700;color:${T.navy};">${escapeHtml(b.h3)}</h3>` : ''}${paragraphsToEmailHtml(b.content, L)}`).join('');
    return `${h2(secTitle(s), `nl-section-${i}`, ep(`section:${i}:h2`))}\n${paragraphsToEmailHtml(secBody(s), L, ep(`section:${i}:content`))}${h3s}`;
  }).join('\n');

  const conclusionHtml = conclusion ? `${h2(L.conclusion, 'nl-conclusion')}\n${paragraphsToEmailHtml(conclusion, L, ep('conclusion'))}` : '';

  const cta = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="margin:28px 0 8px 0;"><tr>
<td align="center" style="background:${T.soft};border:1px solid ${T.border};padding:28px 24px;font-family:${T.font};">
${nl.ctaLead ? `<div${nlAttr(ep('ctaLead'))} style="font-size:17px;line-height:1.5;font-weight:600;color:${T.navy};margin:0 0 14px 0;">${escapeHtml(nl.ctaLead)}</div>` : ''}
${button(ctaLabel, opts.ctaUrl, ep('ctaLabel'))}
</td></tr></table>`;

  const faqHtml = faq.length
    ? `${h2(L.faq, 'nl-faq')}\n${faq.map((f) => `${p(`<strong style="color:${T.navy};">${escapeHtml(f.question || '')}</strong>`, 'margin-bottom:4px;')}${p(escapeHtml(f.answer || ''))}`).join('')}`
    : '';

  const link = (label, url) => url ? `<a href="${escapeHtml(url)}" style="color:${T.muted};text-decoration:underline;">${escapeHtml(label)}</a>` : '';
  const footerLinks = [link(L.webview, opts.webviewUrl), link(L.imprint, opts.imprintUrl), link(L.unsubscribe, unsub)].filter(Boolean).join(' &nbsp;&middot;&nbsp; ');
  const footer = `<tr><td style="padding:24px 32px 36px 32px;border-top:1px solid ${T.border};font-family:${T.font};font-size:12px;line-height:1.6;color:${T.muted};">
<div style="margin:0 0 6px 0;"><strong style="color:${T.navy};">${escapeHtml(brand)}</strong>${opts.senderAddress ? `<br>${escapeHtml(opts.senderAddress).replace(/\n/g, '<br>')}` : ''}</div>
<div style="margin:0 0 10px 0;">${escapeHtml(L.why)}</div>
<div>${footerLinks}</div>
</td></tr>`;

  const inner = `<table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center" class="nl-container" style="width:600px;max-width:100%;background:#ffffff;">
${header}
${heroRow}
<tr><td class="nl-pad" style="padding:28px 32px 8px 32px;">
<h1${nlAttr(ep('title'))} style="margin:0 0 16px 0;font-family:${T.font};font-size:30px;line-height:1.2;font-weight:800;letter-spacing:-0.01em;color:${T.navy};">${escapeHtml(title)}</h1>
${intro ? p(escapeHtml(intro).replace(/\n/g, '<br>'), 'font-size:18px;line-height:1.6;color:#33414f;', ep('intro')) : ''}
${toc}
${takeawayBox}
${body}
${conclusionHtml}
${cta}
${faqHtml}
</td></tr>
${footer}
</table>`;

  const fragment = `${preheader}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#ffffff" style="background:#ffffff;"><tr><td align="center" style="padding:0;">${inner}</td></tr></table>`;
  if (opts.standalone === false) return fragment;

  return `<!DOCTYPE html>
<html lang="${lang}" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="color-scheme" content="light only">
<meta name="supported-color-schemes" content="light only">
<title>${escapeHtml(nl.subject || title)}</title>
<style>
body{margin:0;padding:0;background:#ffffff;-webkit-text-size-adjust:100%;}
img{-ms-interpolation-mode:bicubic;}
${opts.editable ? '[data-nl]{cursor:text;border-radius:3px;transition:background .15s;}[data-nl]:hover{background:rgba(30,158,173,.10);}[data-nl]:focus{outline:2px solid #1e9ead;outline-offset:2px;background:rgba(30,158,173,.06);}' : ''}
${opts.print ? '@page{size:A4;margin:12mm}body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}img,table{page-break-inside:avoid;}h2{page-break-after:avoid;}' : ''}
@media only screen and (max-width:620px){
  .nl-container{width:100% !important;}
  .nl-pad{padding-left:20px !important;padding-right:20px !important;}
  h1{font-size:26px !important;}
  h2{font-size:20px !important;}
}
</style>
</head>
<body style="margin:0;padding:0;background:#ffffff;">
${fragment}
</body>
</html>`;
}

// Plain-text alternative (multipart/alternative) — improves deliverability.
export function buildNewsletterText(blogData, opts = {}) {
  const lang = opts.lang === 'en' ? 'en' : 'de';
  const L = getNewsletterLabels(lang);
  const nl = getNewsletterMeta(blogData);
  // Per block (blank-line separated) so lists, callouts and tables stay apart.
  const strip = (s) => splitBlocks(s).map((block) => {
    const lines = block.split('\n');
    const callout = block.match(/^\s*>\s*(INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):\s*/i);
    if (callout) return `${callout[1].toUpperCase()}: ${block.slice(callout[0].length).replace(/\n>\s?/g, ' ').trim()}`;
    if (lines.length >= 2 && /^\s*\|.*\|\s*$/.test(lines[0]) && /^\s*\|?\s*[-:|\s]+\s*\|?\s*$/.test(lines[1] || '')) {
      return lines.filter((_, i) => i !== 1).map((l) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim()).join(' · ')).join('\n');
    }
    return block.trim();
  }).join('\n\n');
  const out = [];
  out.push((blogData?.meta?.title || opts.fallbackTitle || nl.subject || '').toUpperCase());
  out.push('');
  if (blogData?.intro) out.push(blogData.intro, '');
  const sections = blogData?.sections || [];
  if (sections.length >= 3) {
    out.push(L.toc.toUpperCase());
    sections.forEach((s, i) => out.push(`${i + 1}. ${secTitle(s)}`));
    out.push('');
  }
  const tks = (blogData?.key_takeaways || []).filter(Boolean);
  if (tks.length) {
    out.push(L.takeaways.toUpperCase());
    tks.forEach((k) => out.push(`- ${k}`));
    out.push('');
  }
  sections.forEach((s) => {
    out.push(secTitle(s).toUpperCase(), '', strip(secBody(s)), '');
  });
  if (blogData?.conclusion) out.push(L.conclusion.toUpperCase(), '', strip(blogData.conclusion), '');
  if (nl.ctaLead) out.push(nl.ctaLead);
  out.push(`${nl.ctaLabel || L.defaultCta}: ${opts.ctaUrl || ''}`.trim(), '');
  (blogData?.faq || []).forEach((f) => { if (f?.question) out.push(f.question, f.answer || '', ''); });
  out.push('--', opts.brandName || 'xchannels.ai');
  if (opts.senderAddress) out.push(opts.senderAddress);
  out.push(L.why);
  out.push(`${L.unsubscribe}: ${opts.unsubscribeUrl || '{{unsubscribe_url}}'}`);
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
}


// ── Typing into the preview ────────────────────────────────────────────────
// Writes a text edited inside the preview back into the issue. Paths come from
// the data-nl attributes above; blocks are addressed by their position in the
// blank-line-separated content, exactly as the renderer split them.
const LIST_LINE = /^\s*(?:-|\d+\.)\s+/;

function setBlockText(content, bi, ki, text) {
  const blocks = splitBlocks(content);
  const block = blocks[bi];
  if (block === undefined) return content;
  const callout = block.match(/^\s*>\s*(?:INFO|EINORDNUNG|ZUSAMMENFASSUNG|SUMMARY|BOTTOM\s+LINE|FAZIT|WICHTIG|IMPORTANT|WARNING|ACHTUNG):\s*/i);
  if (ki !== undefined) {
    const lines = block.split('\n');
    const idx = lines.map((l, i) => (LIST_LINE.test(l) ? i : -1)).filter((i) => i >= 0)[ki];
    if (idx !== undefined) {
      const prefix = lines[idx].match(LIST_LINE)[0];
      lines[idx] = prefix + text.replace(/\n+/g, ' ');
      blocks[bi] = lines.join('\n');
    }
  } else if (callout) {
    blocks[bi] = callout[0] + text;
  } else {
    blocks[bi] = text;
  }
  return blocks.join('\n\n');
}

export function applyNewsletterEdit(blogData, path, rawText) {
  const next = JSON.parse(JSON.stringify(blogData || {}));
  const oneLine = String(rawText || '').replace(/\s+/g, ' ').trim();
  const multi = String(rawText || '').replace(/\r/g, '').replace(/\n{2,}/g, '\n').trim();
  const [kind, a, b, c, d] = String(path).split(':');
  next.newsletter = next.newsletter || {};
  switch (kind) {
    case 'title': next.meta = { ...(next.meta || {}), title: oneLine }; break;
    case 'intro': next.intro = multi; break;
    case 'takeaway': (next.key_takeaways = next.key_takeaways || [])[Number(a)] = oneLine; break;
    case 'ctaLead': next.newsletter.cta_lead = oneLine; break;
    case 'ctaLabel': next.newsletter.cta_label = oneLine; break;
    case 'conclusion':
      next.conclusion = a === undefined ? multi : setBlockText(next.conclusion || '', Number(a), b === undefined ? undefined : Number(b), multi);
      break;
    case 'section': {
      const sec = (next.sections || [])[Number(a)];
      if (!sec) break;
      if (b === 'h2') sec.h2 = oneLine;
      else if (b === 'content') sec.content = setBlockText(sec.content || '', Number(c), d === undefined ? undefined : Number(d), multi);
      break;
    }
    default: break;
  }
  return next;
}

// Single-line fields: Enter must not create a line break.
export const isSingleLinePath = (path) => /^(title|takeaway|ctaLead|ctaLabel|section:\d+:h2)/.test(String(path));


// ── "Text" tab: the whole issue as editable text, lossless round trip ──────
// # Überschrift
// Einstieg…
// [Kurz gesagt]
// - Punkt
// ## Abschnittstitel
// Text (Leerzeile = neuer Absatz, "- " Liste, "1. " Schritte, "> INFO: " Hinweis)
// [Fazit]
// Abschluss
// [Button] Button-Text
// [Satz über dem Button] Satz
export function blogToEditableText(blog) {
  const nl = blog?.newsletter || {};
  const out = [`# ${blog?.meta?.title || ''}`, '', blog?.intro || ''];
  const tks = (blog?.key_takeaways || []).filter(Boolean);
  if (tks.length) out.push('', '[Kurz gesagt]', ...tks.map((k) => `- ${k}`));
  (blog?.sections || []).forEach((sec) => out.push('', `## ${sec?.h2 || ''}`, sec?.content || ''));
  if (blog?.conclusion) out.push('', '[Fazit]', blog.conclusion);
  out.push('', `[Button] ${nl.cta_label || ''}`);
  out.push(`[Satz über dem Button] ${nl.cta_lead || ''}`);
  return out.join('\n');
}

export function editableTextToBlog(text, prev) {
  const next = JSON.parse(JSON.stringify(prev || {}));
  next.newsletter = next.newsletter || {};
  next.meta = next.meta || {};
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const bucket = { title: '', intro: [], takeaways: [], conclusion: [], sections: [] };
  let cur = 'intro';
  let sec = null;
  lines.forEach((line) => {
    let m;
    if ((m = line.match(/^#\s+(.*)$/)) && !line.startsWith('##')) { bucket.title = m[1].trim(); cur = 'intro'; return; }
    if ((m = line.match(/^##\s+(.*)$/))) { sec = { h2: m[1].trim(), lines: [] }; bucket.sections.push(sec); cur = 'section'; return; }
    if (/^\[Kurz gesagt\]\s*$/i.test(line)) { cur = 'takeaways'; return; }
    if (/^\[Fazit\]\s*$/i.test(line)) { cur = 'conclusion'; return; }
    if ((m = line.match(/^\[Button\]\s*(.*)$/i))) { next.newsletter.cta_label = m[1].trim(); cur = 'none'; return; }
    if ((m = line.match(/^\[Satz über dem Button\]\s*(.*)$/i))) { next.newsletter.cta_lead = m[1].trim(); cur = 'none'; return; }
    if (cur === 'intro') bucket.intro.push(line);
    else if (cur === 'takeaways') { const t = line.replace(/^\s*-\s*/, '').trim(); if (t) bucket.takeaways.push(t); }
    else if (cur === 'conclusion') bucket.conclusion.push(line);
    else if (cur === 'section' && sec) sec.lines.push(line);
  });
  const join = (arr) => arr.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  next.meta.title = bucket.title;
  next.intro = join(bucket.intro);
  next.key_takeaways = bucket.takeaways;
  next.conclusion = join(bucket.conclusion);
  const old = prev?.sections || [];
  next.sections = bucket.sections.map((b, i) => ({ ...(old[i] || { h3_blocks: [] }), h2: b.h2, content: join(b.lines) }));
  return next;
}
